import React, { useEffect, useState } from "react";
import { useAuth } from "contexts/AuthContext.js";
import CardCreditTransactions from "components/Cards/CardCreditTransactions.js";
import creditService from "services/credits.js";

function formatRefreshDate(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffMs = date - today;
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  const months = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"];
  const dayStr = `${date.getDate()} ${months[date.getMonth()]}`;

  if (diffDays === 0) return "Se renuevan hoy";
  if (diffDays === 1) return `Se renuevan mañana (${dayStr})`;
  return `Se renuevan el ${dayStr} · en ${diffDays} días`;
}

function formatLastUpdate(timestamp) {
  if (!timestamp) return null;
  const diffMs = Date.now() - timestamp;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "hace menos de 1 min";
  if (diffMin === 1) return "hace 1 min";
  if (diffMin < 60) return `hace ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  return `hace ${diffH}h`;
}

export default function Credits() {
  const { user, lastCreditsUpdate, refreshCredits } = useAuth();
  const [refreshing, setRefreshing] = useState(false);
  const [txRefreshTrigger, setTxRefreshTrigger] = useState(0);
  const [nextRefreshDate, setNextRefreshDate] = useState(null);

  const isUnlimited = user?.has_unlimited_credits;
  const balance = user?.credit_balance;
  const monthly = user?.monthly_credits;
  const planName = user?.plan?.name || "FREE";

  useEffect(() => {
    if (!isUnlimited) {
      creditService.getBalance().then((res) => {
        if (res?.data?.next_refresh_date) {
          setNextRefreshDate(res.data.next_refresh_date);
        }
      }).catch(() => {});
    }
  }, [isUnlimited]);

  const percentage =
    !isUnlimited && monthly > 0
      ? Math.min(100, Math.round((balance / monthly) * 100))
      : 100;
  const isLow = !isUnlimited && monthly > 0 && balance < monthly * 0.2;

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshCredits();
      setTxRefreshTrigger((t) => t + 1);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <>
      {/* Header with last-updated info and refresh button */}
      <div className="flex items-center justify-between px-4 mt-4 mb-2">
        <div className="flex items-center gap-2 text-xs text-blueGray-400">
          <i className="fas fa-sync-alt"></i>
          {lastCreditsUpdate ? (
            <span>Actualizado {formatLastUpdate(lastCreditsUpdate)} · Se actualiza automáticamente cada 5 min</span>
          ) : (
            <span>Cargando información de créditos...</span>
          )}
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-1.5 text-xs text-lightBlue-500 hover:text-lightBlue-700 font-semibold disabled:opacity-50 transition-colors"
          title="Actualizar ahora"
        >
          <i className={`fas fa-sync-alt text-xs ${refreshing ? "fa-spin" : ""}`}></i>
          {refreshing ? "Actualizando..." : "Actualizar"}
        </button>
      </div>

      {/* Credit summary cards */}
      <div className="flex flex-wrap mb-4 px-4 gap-4">
        {/* Balance card */}
        <div className="bg-white rounded-lg shadow-md p-5 flex items-center gap-4 min-w-0 flex-1">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-lightBlue-100 flex items-center justify-center">
            <i className="fas fa-coins text-lightBlue-500 text-xl"></i>
          </div>
          <div>
            <p className="text-xs font-bold text-blueGray-400 uppercase tracking-wide mb-0.5">
              Saldo actual
            </p>
            <p
              className={`text-2xl font-bold ${
                isUnlimited
                  ? "text-emerald-600"
                  : isLow
                  ? "text-orange-500"
                  : "text-blueGray-700"
              }`}
            >
              {isUnlimited ? "Ilimitado" : balance}
              {!isUnlimited && (
                <span className="text-sm font-normal text-blueGray-400 ml-1">
                  créditos
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Monthly allowance card */}
        {!isUnlimited && (
          <div className="bg-white rounded-lg shadow-md p-5 flex items-center gap-4 min-w-0 flex-1">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
              <i className="fas fa-calendar-alt text-indigo-500 text-xl"></i>
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold text-blueGray-400 uppercase tracking-wide mb-0.5">
                Plan {planName}
              </p>
              <p className="text-2xl font-bold text-blueGray-700">
                {monthly}
                <span className="text-sm font-normal text-blueGray-400 ml-1">
                  / mes
                </span>
              </p>
              <div className="w-full bg-blueGray-200 rounded-full h-1.5 mt-2">
                <div
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    isLow ? "bg-orange-400" : "bg-lightBlue-400"
                  }`}
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
              <p className="text-xs text-blueGray-400 mt-1">
                {percentage}% restante
              </p>
              {nextRefreshDate && (
                <p className="text-xs text-blueGray-400 mt-1">
                  {formatRefreshDate(nextRefreshDate)}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Plan card (unlimited) */}
        {isUnlimited && (
          <div className="bg-white rounded-lg shadow-md p-5 flex items-center gap-4 min-w-0 flex-1">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
              <i className="fas fa-gem text-emerald-500 text-xl"></i>
            </div>
            <div>
              <p className="text-xs font-bold text-blueGray-400 uppercase tracking-wide mb-0.5">
                Plan
              </p>
              <p className="text-2xl font-bold text-emerald-600">{planName}</p>
              <p className="text-xs text-blueGray-400 mt-0.5">
                Sin límite de créditos
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Transactions table */}
      <div className="flex flex-wrap">
        <div className="w-full mb-12 px-4">
          <CardCreditTransactions refreshTrigger={txRefreshTrigger} />
        </div>
      </div>
    </>
  );
}
